import React from 'react';
import { Grid, Box, Typography, Tooltip, IconButton } from '@mui/material';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import DeleteForeverTwoToneIcon from '@mui/icons-material/DeleteForeverTwoTone';
import { handleToggleDeleteTrainingModel, handleToggleFailedTrainingModal, handleToggleTrainingModelDetailsDialog } from '../../store/mlcreator-slice/mlcreator';
import { AccessTime, HourglassEmpty, Error, Settings } from '@mui/icons-material';
import { styled, keyframes } from '@mui/system';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import TuneIcon from '@mui/icons-material/Tune';
import NavigateIconButton from '../common/NavigateIconButton';
import { AppRoutes } from '../../axios/endPoints';

enum MLAITrainingStatusEnum {
    Waiting = 10,
    Training = 20,
    CalculatingFeatureSignificance = 30,
    Completed = 40,
    Failed = 50
}

interface TrainingStatusActionsProps {
    params: any;
    dispatch: any;
    t: any;
}

const statusColorMap: Record<MLAITrainingStatusEnum, string> = {
    [MLAITrainingStatusEnum.Waiting]: "#787878",
    [MLAITrainingStatusEnum.Training]: "#49c44d",
    [MLAITrainingStatusEnum.CalculatingFeatureSignificance]: "#49c44d",
    [MLAITrainingStatusEnum.Failed]: "#f44336",
    [MLAITrainingStatusEnum.Completed]: ""
};

const pulse = keyframes`
    0% { transform: scale(1); opacity: 0.5; }
    50% { transform: scale(1.1); opacity: 1; }
    100% { transform: scale(1); opacity: 0.5; }
`;

const spin = keyframes`
    100% { transform: rotate(360deg); }
`;

const WaitingIcon = styled(AccessTime)(({ theme }) => ({
    animation: `${pulse} 1s infinite`,
    color: statusColorMap[MLAITrainingStatusEnum.Waiting],
}));

const CalculatingFeatureSignificanceIcon = styled(Settings)(({ theme }) => ({
    animation: `${spin} 1.5s linear infinite`,
    color: statusColorMap[MLAITrainingStatusEnum.CalculatingFeatureSignificance],
}));

const FailedIcon = styled(Error)(({ theme }) => ({
    color: statusColorMap[MLAITrainingStatusEnum.Failed],
}));

const TrainingIcon = styled(Settings)(({ theme }) => ({
    animation: `${spin} 1.5s linear infinite`,
    color: statusColorMap[MLAITrainingStatusEnum.Training],
}));

const getStatusIcon = (status: MLAITrainingStatusEnum) => {
    switch (status) {
        case MLAITrainingStatusEnum.Waiting:
            return <WaitingIcon />;
        case MLAITrainingStatusEnum.CalculatingFeatureSignificance:
            return <CalculatingFeatureSignificanceIcon />;
        case MLAITrainingStatusEnum.Failed:
            return <FailedIcon />;
        case MLAITrainingStatusEnum.Training:
            return <TrainingIcon />;
        default:
            return null;
    }
};
const Old_TrainingStatusActions: React.FC<TrainingStatusActionsProps> = ({ params, dispatch, t }) => {
    const renderStatus = (
        icon: React.ReactNode,
        text: string,
        onClick?: () => void
    ) => (
        <Grid container spacing={1} justifyContent="center" alignItems="center">
            <Grid item>
                <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    onClick={onClick}
                    style={{ cursor: onClick ? 'pointer' : 'default' }}
                >
                    {icon}
                    <Typography variant="body2">{t(text)}</Typography>
                </Box>
            </Grid>
        </Grid>
    );

    const renderFailedActions = (text: string) => (
        <Grid container spacing={3} alignItems="center">
            <Grid item xs={6} />
            <Grid item xs={3}>
                <Tooltip title={t('FAILED_ICON_TOOLTIP')}>
                    <Box display="flex" alignItems="center">
                        <IconButton onClick={() => dispatch(handleToggleFailedTrainingModal({
                            isOpen: true,
                            body: { message: t(params.row.trainingResponseError), description: params.row.trainingResponseErrorDetails }
                        }))}>
                            <FailedIcon />
                        </IconButton>
                    </Box>
                </Tooltip>
            </Grid>
            <Grid item xs={3}>
                <Tooltip title={t('CREATOR_INNER_DELETE_ACTION')}>
                    <Box display="flex" alignItems="center">
                        <IconButton onClick={() => dispatch(handleToggleDeleteTrainingModel({
                            isOpen: true,
                            body: {
                                name: params.row.name,
                                id: params.row.id
                            }
                        }))}>
                            <DeleteForeverTwoToneIcon className="delete-icon" />
                        </IconButton>
                    </Box>
                </Tooltip>
            </Grid>
        </Grid>
    );

    const renderCompletedActions = () => (
        <Grid container spacing={3}>
            <Grid item xs={3} style={{ visibility: params.row.canBePredicted ? 'visible' : 'hidden' }}>
                <NavigateIconButton to={`${AppRoutes.ML_PREDICTION}?id=${params.row.id}&name=${params.row.name}`}>
                    <Tooltip title={t('CREATOR_INNER_REDIRECT_TO_PREDICTIONS_ACTION')}>
                        <AnalyticsIcon className="redirect-icon" />
                    </Tooltip>
                </NavigateIconButton>
            </Grid>
            <Grid item xs={3} style={{ visibility: params.row.canBeOptimised ? 'visible' : 'hidden' }}>
                <NavigateIconButton to={`${AppRoutes.ML_OPTIMIZATION}?id=${params.row.id}&name=${params.row.name}`}>
                    <Tooltip title={t('CREATOR_INNER_REDIRECT_TO_OPTIMIZATION_ACTION')}>
                        <TuneIcon className="optimization-icon" />
                    </Tooltip>
                </NavigateIconButton>
            </Grid>
            <Grid item xs={3}>
                <Tooltip title={t('CREATOR_INNER_DETAILS_ACTION')}>
                    <IconButton onClick={() => dispatch(handleToggleTrainingModelDetailsDialog({
                        isOpen: true,
                        body: {
                            id: params.row.id,
                            name: params.row.name
                        }
                    }))}>
                        <InfoTwoToneIcon className="info-icon" />
                    </IconButton>
                </Tooltip>
            </Grid>
            <Grid item xs={3}>
                <Tooltip title={t('CREATOR_INNER_DELETE_ACTION')}>
                    <IconButton onClick={() => {
                        dispatch(handleToggleDeleteTrainingModel({
                            isOpen: true,
                            body: {
                                name: params.row.name,
                                id: params.row.id
                            }
                        }))
                    }}>
                        <DeleteForeverTwoToneIcon className="delete-icon" />
                    </IconButton>
                </Tooltip>
            </Grid>
        </Grid>
    );

    switch (params.row.trainingStatus) {
        case MLAITrainingStatusEnum.Waiting:
        case MLAITrainingStatusEnum.CalculatingFeatureSignificance:
        case MLAITrainingStatusEnum.Training:
            return renderStatus(
                getStatusIcon(params.row.trainingStatus),
                params.row.trainingStatusText
            );

        case MLAITrainingStatusEnum.Failed:
            return renderFailedActions(params.row.trainingStatusText);

        case MLAITrainingStatusEnum.Completed:
            return renderCompletedActions();

        default:
            return null;
    }
};

export default Old_TrainingStatusActions;